import type {
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
} from "./element/types";
import type { BinaryFiles } from "./types";
import type { Spreadsheet } from "./charts";
import { tryParseSpreadsheet, VALID_SPREADSHEET } from "./charts";
import {
  ALLOWED_PASTE_MIME_TYPES,
  EXPORT_DATA_TYPES,
  MIME_TYPES,
} from "./constants";
import {
  isFrameLikeElement,
  isInitializedImageElement,
} from "./element/typeChecks";
import { deepCopyElement } from "./element/newElement";
import { mutateElement } from "./element/mutateElement";
import { getContainingFrame } from "./frame";
import { arrayToMap, isMemberOf, isPromiseLike } from "./utils";
import { createFile, isSupportedImageFileType } from "./data/blob";
import { ExcalidrawError } from "./errors";

type ElementsClipboard = {
  type: typeof EXPORT_DATA_TYPES.excalidrawClipboard;
  elements: readonly NonDeletedExcalidrawElement[];
  files: BinaryFiles | undefined;
};

export type PastedMixedContentKind =
  | "heading1"
  | "heading2"
  | "heading3"
  | "listItem"
  | "paragraph";

export type PastedMixedContent = {
  type: "text" | "imageUrl";
  value: string;
  /** only set for `type: "text"` entries parsed out of HTML clipboard data */
  kind?: PastedMixedContentKind;
  /** only set when `kind === "listItem"` */
  ordered?: boolean;
}[];

export interface ClipboardData {
  spreadsheet?: Spreadsheet;
  elements?: readonly ExcalidrawElement[];
  files?: BinaryFiles;
  text?: string;
  mixedContent?: PastedMixedContent;
  errorMessage?: string;
  programmaticAPI?: boolean;
}

type AllowedPasteMimeTypes = typeof ALLOWED_PASTE_MIME_TYPES[number];

type ParsedClipboardEventTextData =
  | { type: "text"; value: string }
  | { type: "mixedContent"; value: PastedMixedContent };

export const probablySupportsClipboardReadText =
  "clipboard" in navigator && "readText" in navigator.clipboard;

export const probablySupportsClipboardWriteText =
  "clipboard" in navigator && "writeText" in navigator.clipboard;

export const probablySupportsClipboardBlob =
  "clipboard" in navigator &&
  "write" in navigator.clipboard &&
  "ClipboardItem" in window &&
  "toBlob" in HTMLCanvasElement.prototype;

const clipboardContainsElements = (
  contents: any,
): contents is { elements: ExcalidrawElement[]; files?: BinaryFiles } => {
  if (
    [
      EXPORT_DATA_TYPES.excalidraw,
      EXPORT_DATA_TYPES.excalidrawClipboard,
      EXPORT_DATA_TYPES.excalidrawClipboardWithAPI,
    ].includes(contents?.type) &&
    Array.isArray(contents.elements)
  ) {
    return true;
  }
  return false;
};

export const createPasteEvent = ({
  types,
  files,
}: {
  types?: { [key in AllowedPasteMimeTypes]?: string | File };
  files?: File[];
}) => {
  if (!types && !files) {
    console.warn("createPasteEvent: no types or files provided");
  }

  const event = new ClipboardEvent("paste", {
    clipboardData: new DataTransfer(),
  });

  if (types) {
    for (const [type, value] of Object.entries(types)) {
      if (typeof value !== "string") {
        files = files || [];
        files.push(value);
        continue;
      }
      try {
        event.clipboardData?.setData(type, value);
        if (event.clipboardData?.getData(type) !== value) {
          throw new Error(`Failed to set "${type}" as clipboardData item`);
        }
      } catch (error: any) {
        throw new Error(error.message);
      }
    }
  }

  if (files) {
    let idx = -1;
    for (const file of files) {
      idx++;
      try {
        event.clipboardData?.items.add(file);
        if (event.clipboardData?.files[idx] !== file) {
          throw new Error(
            `Failed to set file "${file.name}" as clipboardData item`,
          );
        }
      } catch (error: any) {
        throw new Error(error.message);
      }
    }
  }

  return event;
};

export const serializeAsClipboardJSON = ({
  elements,
  files,
}: {
  elements: readonly NonDeletedExcalidrawElement[];
  files: BinaryFiles | null;
}) => {
  const elementsMap = arrayToMap(elements);
  const framesToCopy = new Set(
    elements.filter((element) => isFrameLikeElement(element)),
  );
  let foundFile = false;

  const _files = elements.reduce((acc, element) => {
    if (isInitializedImageElement(element)) {
      foundFile = true;
      if (files && files[element.fileId]) {
        acc[element.fileId] = files[element.fileId];
      }
    }
    return acc;
  }, {} as BinaryFiles);

  if (foundFile && !files) {
    console.warn(
      "copyToClipboard: attempting to file element(s) without providing associated `files` object.",
    );
  }

  // select bound text elements when copying
  const contents: ElementsClipboard = {
    type: EXPORT_DATA_TYPES.excalidrawClipboard,
    elements: elements.map((element) => {
      if (
        getContainingFrame(element, elementsMap) &&
        !framesToCopy.has(getContainingFrame(element, elementsMap)!)
      ) {
        const copiedElement = deepCopyElement(element);
        mutateElement(copiedElement, {
          frameId: null,
        });
        return copiedElement;
      }

      return element;
    }),
    files: files ? _files : undefined,
  };

  return JSON.stringify(contents);
};

export const copyToClipboard = async (
  elements: readonly NonDeletedExcalidrawElement[],
  files: BinaryFiles | null,
  /** supply if available to make the operation more certain to succeed */
  clipboardEvent?: ClipboardEvent | null,
) => {
  await copyTextToSystemClipboard(
    serializeAsClipboardJSON({ elements, files }),
    clipboardEvent,
  );
};

const parsePotentialSpreadsheet = (
  text: string,
): { spreadsheet: Spreadsheet } | { errorMessage: string } | null => {
  const result = tryParseSpreadsheet(text);
  if (result.type === VALID_SPREADSHEET) {
    return { spreadsheet: result.spreadsheet };
  }
  return null;
};

const BLOCK_TAG_NAMES = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "LI"]);

const isBlockElement = (node: ChildNode): node is HTMLElement =>
  node instanceof HTMLElement && BLOCK_TAG_NAMES.has(node.tagName);

const normalizeWhitespace = (text: string) => text.replace(/\s+/g, " ").trim();

const parseFontWeight = (value: string): number => {
  if (!value) {
    return 0;
  }
  if (value === "bold") {
    return 700;
  }
  if (value === "normal") {
    return 400;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// crude px->pt conversion (96dpi assumption) — this is a heuristic for
// detecting headings, not an attempt at a pixel-accurate copy of the source
const parseFontSizeToPt = (value: string): number => {
  const match = value?.match(/^([\d.]+)(pt|px)$/);
  if (!match) {
    return 0;
  }
  const num = parseFloat(match[1]);
  return match[2] === "px" ? num * 0.75 : num;
};

/**
 * Google Docs (and most rich text editors) encode formatting via inline
 * `style` attributes (e.g. `font-weight:700`, `font-size:20pt`), not
 * semantic tags — while other sources (e.g. HTML generated from markdown,
 * such as ChatGPT's copy button turning **bold** into `<strong>`) do the
 * opposite and never set an inline style at all. Both sources are checked
 * for every descendant, otherwise either kind of paste would silently fail
 * to be detected as containing formatting.
 */
const getDominantStyleWithinBlock = (
  el: Element,
): { fontWeight: number; fontSizePt: number } => {
  let fontWeight = 0;
  let fontSizePt = 0;

  const walk = (node: Element) => {
    const style = (node as HTMLElement).style;
    if (style) {
      fontWeight = Math.max(fontWeight, parseFontWeight(style.fontWeight));
      fontSizePt = Math.max(fontSizePt, parseFontSizeToPt(style.fontSize));
    }
    if (node.tagName === "B" || node.tagName === "STRONG") {
      fontWeight = Math.max(fontWeight, 700);
    }
    for (const child of node.children) {
      walk(child);
    }
  };

  walk(el);

  return { fontWeight, fontSizePt };
};

const getBlockKind = (
  el: HTMLElement,
  ordered: boolean,
): { kind: PastedMixedContentKind; ordered?: boolean } => {
  if (el.tagName === "LI") {
    return { kind: "listItem", ordered };
  }
  if (el.tagName === "H1") {
    return { kind: "heading1" };
  }
  if (el.tagName === "H2") {
    return { kind: "heading2" };
  }
  if (/^H[3-6]$/.test(el.tagName)) {
    return { kind: "heading3" };
  }

  const { fontWeight, fontSizePt } = getDominantStyleWithinBlock(el);
  if (fontSizePt >= 20) {
    return { kind: "heading1" };
  }
  if (fontSizePt >= 16) {
    return { kind: "heading2" };
  }
  if (fontSizePt >= 13 || fontWeight >= 700) {
    return { kind: "heading3" };
  }

  return { kind: "paragraph" };
};

/**
 * Collects the block's own inline text (text nodes + inline elements like
 * <span>/<b>/<a>). Nested UL/OL are skipped — those are walked separately by
 * the caller so their text isn't duplicated into the parent's entry. Other
 * nested block tags (e.g. Google Docs wraps each <li>'s content in its own
 * <p role="presentation">) are NOT skipped: they're not independently
 * reachable by any other recursion in this file, so their text would
 * otherwise be silently dropped — confirmed by testing against real
 * Google Docs clipboard HTML, which nests exactly this way.
 */
const getInlineTextContent = (node: ChildNode): string => {
  let text = "";
  for (const child of node.childNodes) {
    if (child.nodeType === 3) {
      text += child.textContent || "";
    } else if (child instanceof HTMLElement && child.tagName === "BR") {
      text += " ";
    } else if (
      child instanceof HTMLElement &&
      child.tagName !== "UL" &&
      child.tagName !== "OL" &&
      child.tagName !== "IMG"
    ) {
      text += getInlineTextContent(child);
    }
    // nested UL/OL are skipped here — lists are handled by the caller's own
    // recursion. IMG has no text content of its own; image *detection*
    // happens separately via findImageUrls (dropping inline images inside a
    // formatted paste is an accepted trade-off, see plan — but they must
    // still be detected so the "images were skipped" toast can fire).
  }
  return text;
};

/**
 * Whether a <img src="..."> value is worth carrying through as an
 * `imageUrl` entry. Accepts `http(s)` (existing behavior) and `data:image/`
 * (base64-inline images — the common case for real Google Docs/AI-chat
 * clipboard pastes, previously silently invisible here entirely). Excludes
 * `data:image/svg+xml`: an inline SVG can carry a <script>, and paste would
 * otherwise be a new attack vector that doesn't exist today (today that
 * requires a real http-hosted image). This only gates by MIME type — size
 * limits for `data:` payloads are enforced later, per-URL, in
 * `insertImagesFromUrls` (App.tsx), where a toast can actually be shown.
 */
const isUsableImageSrc = (url: string): boolean =>
  url.startsWith("http") ||
  (url.startsWith("data:image/") && !url.startsWith("data:image/svg+xml"));

/**
 * Google Docs wraps an inline image in its own <p>, e.g. <p><img src=...></p>
 * — same nesting problem as <li><p> above. This finds such images so the
 * caller can at least warn the user they were dropped, even though the
 * top-level `node instanceof HTMLImageElement` check in parseHTMLTree only
 * catches an <img> that's a direct, unwrapped child.
 */
const findImageUrls = (node: Element): string[] => {
  const urls: string[] = [];
  for (const child of node.children) {
    if (child instanceof HTMLImageElement) {
      const url = child.getAttribute("src");
      if (url && isUsableImageSrc(url)) {
        urls.push(url);
      }
    } else if (child.tagName !== "UL" && child.tagName !== "OL") {
      urls.push(...findImageUrls(child));
    }
  }
  return urls;
};

/**
 * Whether `node` has a block-level (P/H1-6/LI) or list (UL/OL) descendant
 * anywhere within it — used to decide whether a plain wrapper (DIV, or any
 * unrecognized tag) needs recursing into to find those nested blocks, versus
 * being treated as one run of loose inline content (see parseHTMLTree).
 */
const containsBlockOrList = (node: Element): boolean => {
  for (const child of node.children) {
    if (
      isBlockElement(child) ||
      child.tagName === "UL" ||
      child.tagName === "OL" ||
      containsBlockOrList(child)
    ) {
      return true;
    }
  }
  return false;
};

/** internal, specific to parsing paste events. Do not reuse. */
function parseHTMLTree(el: ChildNode, ordered = false): PastedMixedContent {
  let result: PastedMixedContent = [];

  // Accumulates "loose" inline content — text/inline elements that are
  // siblings with no <p>/<h>/<li> wrapper around them. Chat-AI tools (e.g.
  // Perplexity-style citations) commonly emit exactly this: a bare text
  // node "[", an <a>1</a>, text ",", <a>2</a>, text "]" as siblings with no
  // block wrapper. Without buffering, each sibling became its own line —
  // a real, reported bug (citation markers exploding into single-character
  // lines) against real-world AI-chat clipboard HTML.
  let inlineBuffer = "";

  const flushInlineBuffer = () => {
    const text = normalizeWhitespace(inlineBuffer);
    inlineBuffer = "";
    if (text) {
      result.push({ type: "text", value: text, kind: "paragraph" });
    }
  };

  for (const node of el.childNodes) {
    if (node.nodeType === 3) {
      inlineBuffer += node.textContent || "";
    } else if (node instanceof HTMLImageElement) {
      flushInlineBuffer();
      const url = node.getAttribute("src");
      if (url && isUsableImageSrc(url)) {
        result.push({ type: "imageUrl", value: url });
      }
    } else if (isBlockElement(node)) {
      flushInlineBuffer();
      const text = normalizeWhitespace(getInlineTextContent(node));
      if (text) {
        result.push({
          type: "text",
          value: text,
          ...getBlockKind(node, ordered),
        });
      }
      // an <img> nested inside this block (e.g. Google Docs' <p><img></p>)
      // is invisible to the top-level `instanceof HTMLImageElement` check
      // above, since that only fires for an <img> that's a direct child of
      // `el` — find it here so it's still reported (and the "images were
      // skipped" toast still fires) even though it won't be inserted.
      for (const url of findImageUrls(node)) {
        result.push({ type: "imageUrl", value: url });
      }
      // a block can itself wrap a nested list (e.g. <li>text<ul>...</ul></li>)
      for (const child of node.childNodes) {
        if (child instanceof HTMLElement && child.tagName === "OL") {
          result = result.concat(parseHTMLTree(child, true));
        } else if (child instanceof HTMLElement && child.tagName === "UL") {
          result = result.concat(parseHTMLTree(child, false));
        }
      }
    } else if (node instanceof HTMLElement && node.tagName === "OL") {
      flushInlineBuffer();
      result = result.concat(parseHTMLTree(node, true));
    } else if (node instanceof HTMLElement && node.tagName === "UL") {
      flushInlineBuffer();
      result = result.concat(parseHTMLTree(node, false));
    } else if (node instanceof HTMLElement && node.tagName === "BR") {
      inlineBuffer += " ";
    } else if (node instanceof HTMLElement && containsBlockOrList(node)) {
      // a mixed wrapper (e.g. a <div> with both loose text and a nested
      // <p>) — flush what came before it, recurse to find the nested
      // blocks/lists, then keep buffering afterwards
      flushInlineBuffer();
      result = result.concat(parseHTMLTree(node, ordered));
    } else if (node instanceof HTMLElement) {
      // pure inline wrapper (span/b/a/sup/em/...) with no block
      // descendants — its text belongs to the same loose run as its
      // siblings, e.g. the <a>1</a> inside a bare "[1, 2]" citation
      inlineBuffer += getInlineTextContent(node);
    }
  }

  flushInlineBuffer();
  return result;
}

const maybeParseHTMLPaste = (
  event: ClipboardEvent,
): { type: "mixedContent"; value: PastedMixedContent } | null => {
  const html = event.clipboardData?.getData(MIME_TYPES.html);

  if (!html) {
    return null;
  }

  try {
    const doc = new DOMParser().parseFromString(html, MIME_TYPES.html);

    const content = parseHTMLTree(doc.body);

    if (content.length) {
      return { type: "mixedContent", value: content };
    }
  } catch (error: any) {
    console.error(`error in parseHTMLFromPaste: ${error.message}`);
  }

  return null;
};

/**
 * Reads OS clipboard programmatically. May not work on all browsers.
 * Will prompt user for permission if not granted.
 */
export const readSystemClipboard = async () => {
  const types: { [key in AllowedPasteMimeTypes]?: string | File } = {};

  let clipboardItems: ClipboardItems;

  try {
    clipboardItems = await navigator.clipboard?.read();
  } catch (error: any) {
    try {
      if (navigator.clipboard?.readText) {
        console.warn(
          `navigator.clipboard.readText() failed (${error.message}). Failling back to navigator.clipboard.read()`,
        );
        const readText = await navigator.clipboard?.readText();
        if (readText) {
          return { [MIME_TYPES.text]: readText };
        }
      }
    } catch (error: any) {
      // @ts-ignore
      if (navigator.clipboard?.read) {
        console.warn(
          `navigator.clipboard.readText() failed (${error.message}). Failling back to navigator.clipboard.read()`,
        );
      } else {
        if (error.name === "DataError") {
          console.warn(
            `navigator.clipboard.read() error, clipboard is probably empty: ${error.message}`,
          );
          return types;
        }

        throw error;
      }
    }
    throw error;
  }

  for (const item of clipboardItems) {
    for (const type of item.types) {
      if (!isMemberOf(ALLOWED_PASTE_MIME_TYPES, type)) {
        continue;
      }
      try {
        if (type === MIME_TYPES.text || type === MIME_TYPES.html) {
          types[type] = await (await item.getType(type)).text();
        } else if (isSupportedImageFileType(type)) {
          const imageBlob = await item.getType(type);
          const file = createFile(imageBlob, type, undefined);
          types[type] = file;
        } else {
          throw new ExcalidrawError(`Unsupported clipboard type: ${type}`);
        }
      } catch (error: any) {
        console.warn(
          error instanceof ExcalidrawError
            ? error.message
            : `Cannot retrieve ${type} from clipboardItem: ${error.message}`,
        );
      }
    }
  }

  if (Object.keys(types).length === 0) {
    console.warn("No clipboard data found from clipboard.read().");
    return types;
  }

  return types;
};

/**
 * Parses "paste" ClipboardEvent.
 */
const parseClipboardEventTextData = async (
  event: ClipboardEvent,
  isPlainPaste = false,
): Promise<ParsedClipboardEventTextData> => {
  try {
    const mixedContent = !isPlainPaste && event && maybeParseHTMLPaste(event);

    if (mixedContent) {
      // headings/list items carry formatting worth preserving as a grouped
      // block — don't collapse those to plain text even without images
      const hasFormatting = mixedContent.value.some(
        (item) => item.type === "text" && item.kind && item.kind !== "paragraph",
      );

      if (!hasFormatting && mixedContent.value.every((item) => item.type === "text")) {
        return {
          type: "text",
          value:
            event.clipboardData?.getData(MIME_TYPES.text) ||
            mixedContent.value
              .map((item) => item.value)
              .join("\n")
              .trim(),
        };
      }

      return mixedContent;
    }

    const text = event.clipboardData?.getData(MIME_TYPES.text);

    return { type: "text", value: (text || "").trim() };
  } catch {
    return { type: "text", value: "" };
  }
};

/**
 * Attempts to parse clipboard event.
 */
export const parseClipboard = async (
  event: ClipboardEvent,
  isPlainPaste = false,
): Promise<ClipboardData> => {
  const parsedEventData = await parseClipboardEventTextData(
    event,
    isPlainPaste,
  );

  if (parsedEventData.type === "mixedContent") {
    return {
      mixedContent: parsedEventData.value,
    };
  }

  try {
    // if system clipboard contains spreadsheet, use it even though it's
    // technically possible it's staler than in-app clipboard
    const spreadsheetResult =
      !isPlainPaste && parsePotentialSpreadsheet(parsedEventData.value);

    if (spreadsheetResult) {
      return spreadsheetResult;
    }
  } catch (error: any) {
    console.error(error);
  }

  try {
    const systemClipboardData = JSON.parse(parsedEventData.value);
    const programmaticAPI =
      systemClipboardData.type === EXPORT_DATA_TYPES.excalidrawClipboardWithAPI;
    if (clipboardContainsElements(systemClipboardData)) {
      return {
        elements: systemClipboardData.elements,
        files: systemClipboardData.files,
        text: isPlainPaste
          ? JSON.stringify(systemClipboardData.elements, null, 2)
          : undefined,
        programmaticAPI,
      };
    }
  } catch {}

  return { text: parsedEventData.value };
};

export const copyBlobToClipboardAsPng = async (blob: Blob | Promise<Blob>) => {
  try {
    // in Safari so far we need to construct the ClipboardItem synchronously
    // (i.e. in the same tick) otherwise browser will complain for lack of
    // user intent. Using a Promise ClipboardItem constructor solves this.
    // https://bugs.webkit.org/show_bug.cgi?id=222262
    //
    // Note that Firefox (and potentially others) seems to support Promise
    // ClipboardItem constructor, but throws on an unrelated MIME type error.
    // So we need to await this and fallback to awaiting the blob if applicable.
    await navigator.clipboard.write([
      new window.ClipboardItem({
        [MIME_TYPES.png]: blob,
      }),
    ]);
  } catch (error: any) {
    // if we're using a Promise ClipboardItem, let's try constructing
    // with resolution value instead
    if (isPromiseLike(blob)) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          [MIME_TYPES.png]: await blob,
        }),
      ]);
    } else {
      throw error;
    }
  }
};

export const copyTextToSystemClipboard = async (
  text: string | null,
  clipboardEvent?: ClipboardEvent | null,
) => {
  // (1) first try using Async Clipboard API
  if (probablySupportsClipboardWriteText) {
    try {
      // NOTE: doesn't work on FF on non-HTTPS domains, or when document
      // not focused
      await navigator.clipboard.writeText(text || "");
      return;
    } catch (error: any) {
      console.error(error);
    }
  }

  // (2) if fails and we have access to ClipboardEvent, use plain old setData()
  try {
    if (clipboardEvent) {
      clipboardEvent.clipboardData?.setData(MIME_TYPES.text, text || "");
      if (clipboardEvent.clipboardData?.getData(MIME_TYPES.text) !== text) {
        throw new Error("Failed to setData on clipboardEvent");
      }
      return;
    }
  } catch (error: any) {
    console.error(error);
  }

  // (3) if that fails, use document.execCommand
  if (!copyTextViaExecCommand(text)) {
    throw new Error("Error copying to clipboard.");
  }
};

// adapted from https://github.com/zenorocha/clipboard.js/blob/ce79f170aa655c408b6aab33c9472e8e4fa52e19/src/clipboard-action.js#L48
const copyTextViaExecCommand = (text: string | null) => {
  // execCommand doesn't allow copying empty strings, so if we're
  // clearing clipboard using this API, we must copy at least an empty char
  if (!text) {
    text = " ";
  }

  const isRTL = document.documentElement.getAttribute("dir") === "rtl";

  const textarea = document.createElement("textarea");

  textarea.style.border = "0";
  textarea.style.padding = "0";
  textarea.style.margin = "0";
  textarea.style.position = "absolute";
  textarea.style[isRTL ? "right" : "left"] = "-9999px";
  const yPosition = window.pageYOffset || document.documentElement.scrollTop;
  textarea.style.top = `${yPosition}px`;
  // Prevent zooming on iOS
  textarea.style.fontSize = "12pt";

  textarea.setAttribute("readonly", "");
  textarea.value = text;

  document.body.appendChild(textarea);

  let success = false;

  try {
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    success = document.execCommand("copy");
  } catch (error: any) {
    console.error(error);
  }

  textarea.remove();

  return success;
};
